import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/data";
import { remove } from "aws-amplify/storage";
import { QRCodeSVG } from "qrcode.react";
import PreRegistrationApproval from "./PreRegistrationApproval";
import CompetitionSettingsForm from "./CompetitionSettingsForm";
import { useAuth } from "../../contexts/AuthContext";
import { parseLambdaResponse } from "../../utils/parseLambdaResponse";
import { extractStorageKey } from "../../utils/storageImageUrl";
import StorageImage from "../StorageImage";
import ConfirmActionDialog from "./ConfirmActionDialog";
import { toClubInputValue, fromClubInputValue, getClubTimezoneAbbreviation } from "../../utils/clubTime";
import { deriveCompetitionTimes, deriveLifecycle, type Phase } from "@shared/competitionRules";
import { formatClubDateTime } from '../../utils/clubTime';

// Use Cognito user pool auth for admin operations (updates/deletes require authentication)
const client = generateClient({ authMode: 'userPool' });

const PHASE_LABEL: Record<Phase, string> = {
  CANCELLED: 'Cancelled',
  PUBLISHED: 'Published',
  ADJUDICATED: 'Adjudicated',
  TIMING_INCOMPLETE: 'Timing incomplete',
  ENDED: 'Ended',
  FISHING: 'Fishing',
  SIGN_ON: 'Sign-on open',
  SCHEDULED: 'Scheduled',
};

const PHASE_COLOUR: Record<Phase, string> = {
  CANCELLED: 'bg-red-100 text-red-800',
  PUBLISHED: 'bg-purple-100 text-purple-800',
  ADJUDICATED: 'bg-blue-100 text-blue-800',
  TIMING_INCOMPLETE: 'bg-red-100 text-red-800',
  ENDED: 'bg-yellow-100 text-yellow-800',
  FISHING: 'bg-green-100 text-green-800',
  SIGN_ON: 'bg-teal-100 text-teal-800',
  SCHEDULED: 'bg-gray-100 text-gray-800',
};

interface Event {
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  eventDate: string;
  location?: string;
  address?: string;
  maxAttendees?: number;
  currentAttendees: number;
  eventType: "MEETING" | "COMPETITION" | "SOCIAL" | "TRAINING" | "TRIP";
  isActive: boolean;
  cost?: number;
  nonMemberPrice?: number;
  allowNonMembers: boolean;
  currency: string;
  imageUrl?: string;
  organizerName?: string;
  equipment?: string;
  registrationOpenTime?: string;
  registrationCloseTime?: string;
  // Set on the competition settings form (CompetitionSettingsForm), not this form. Read here only
  // to warn when they are missing — see the "Timing incomplete" badge, and
  // to derive the row's phase chip.
  competitionStartTime?: string | null;
  competitionEndTime?: string | null;
  gracePeriodMinutes?: number | null;
  adjudicatedAt?: string | null;
  publishedAt?: string | null;
  cancelledAt?: string | null;
  preRegistrationEnabled?: boolean;
  preRegistrationDeadline?: string;
  createdAt: string;
  updatedAt: string;
}

type EventFormData = Omit<
  Event,
  "id" | "createdAt" | "updatedAt" | "currentAttendees"
>;

const EventManagement: React.FC = () => {
  const { userId } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  // Sunday hardening (16 Oct 2026): the Activate/Deactivate row control asks
  // first; the handler itself is unchanged. The Cancel override control
  // (#134 D4) is gone along with the override flag it toggled.
  const [confirmAction, setConfirmAction] = useState<{ event: Event } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  // #134 D4: Reschedule moves eventDate and re-derives every dependent time,
  // via rescheduleTimes, rather than leaving them to silently disagree
  // (the gap this closes: editing eventDate alone did not shift
  // competitionStartTime/EndTime or the registration window).
  const [rescheduleEvent, setRescheduleEvent] = useState<Event | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [registrationCounts, setRegistrationCounts] = useState<
    Record<string, { pending: number; total: number }>
  >({});
  const [preRegistrationCounts, setPreRegistrationCounts] = useState<
    Record<string, { pending: number; total: number }>
  >({});
  const [showPreRegModal, setShowPreRegModal] = useState<{
    eventId: string;
    eventTitle: string;
    eventDate: string;
  } | null>(null);
  const [adjudicationEvent, setAdjudicationEvent] = useState<Event | null>(null);
  const [showCatchQr, setShowCatchQr] = useState(false);
  // Delete protection (R7.3): counts of CompetitionDayRegistration,
  // CatchSubmission and EventPreRegistration rows for each event, so the
  // Delete button can be disabled up front with an explanation. Re-checked
  // at click time in handleDelete rather than trusted blindly, since this
  // count can go stale between page load and the click.
  const [deleteBlockCounts, setDeleteBlockCounts] = useState<
    Record<string, { registrations: number; catches: number; preRegistrations: number }>
  >({});
  const [deleting, setDeleting] = useState<string | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [formData, setFormData] = useState<EventFormData>({
    title: "",
    description: "",
    shortDescription: "",
    eventDate: "",
    location: "Riverside Drive opposite Tumbulgum Tavern",
    address: "",
    maxAttendees: undefined,
    eventType: "SOCIAL",
    isActive: true,
    cost: 10, // Default member cost to $10
    nonMemberPrice: undefined,
    allowNonMembers: false,
    currency: "AUD",
    imageUrl: "",
    organizerName: "",
    equipment: "",
    registrationOpenTime: "",
    registrationCloseTime: "",
    preRegistrationEnabled: false,
    preRegistrationDeadline: "",
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadRegistrationCounts = async (eventIds: string[]) => {
    try {
      const counts: Record<string, { pending: number; total: number }> = {};
      const preCounts: Record<string, { pending: number; total: number }> = {};
      const blockCounts: Record<string, { registrations: number; catches: number; preRegistrations: number }> = {};

      for (const eventId of eventIds) {
        // Load CompetitionDayRegistration counts. Paginated: a filtered list()
        // caps rows SCANNED, not returned, so a single call under-reports and the
        // badge would show fewer check-ins than exist. Same fault fixed in
        // CompetitionDayDashboard and AdjudicationDashboard on 20 Sep 2026.
        let regRows: any[] = [];
        let regToken: string | null = null;
        do {
          const page: any = await (client.models.CompetitionDayRegistration as any).list({
            filter: { eventId: { eq: eventId } },
            limit: 1000,
            ...(regToken ? { nextToken: regToken } : {}),
          });
          regRows = regRows.concat(page.data || []);
          regToken = page.nextToken ?? null;
        } while (regToken);

        {
          const pending = regRows.filter(
            (r: any) => r.registrationStatus === "PENDING",
          ).length;
          const total = regRows.length;
          counts[eventId] = { pending, total };
        }

        // Load EventPreRegistration counts (use authenticated client)
        const authClient = generateClient({ authMode: 'userPool' });
        let preRegRows: any[] = [];
        let preToken: string | null = null;
        do {
          const page: any = await (authClient.models.EventPreRegistration as any).list({
            filter: { eventId: { eq: eventId } },
            limit: 1000,
            ...(preToken ? { nextToken: preToken } : {}),
          });
          preRegRows = preRegRows.concat(page.data || []);
          preToken = page.nextToken ?? null;
        } while (preToken);

        const preRegResult = { data: preRegRows };
        if (preRegResult.data) {
          const prePending = preRegResult.data.filter(
            (r: any) => r.status === "PENDING",
          ).length;
          const preTotal = preRegResult.data.length;
          preCounts[eventId] = { pending: prePending, total: preTotal };
        }

        // Delete protection (R7.3): CatchSubmission count via the eventId
        // GSI, paginated the same way — a filtered list() caps rows
        // SCANNED, not returned, so a single page can under-report and
        // wrongly allow a delete that should be refused.
        let catchRows: any[] = [];
        let catchToken: string | null = null;
        do {
          const page: any = await (client.models.CatchSubmission as any).list({
            filter: { eventId: { eq: eventId } },
            limit: 1000,
            ...(catchToken ? { nextToken: catchToken } : {}),
          });
          catchRows = catchRows.concat(page.data || []);
          catchToken = page.nextToken ?? null;
        } while (catchToken);

        blockCounts[eventId] = {
          registrations: regRows.length,
          catches: catchRows.length,
          preRegistrations: preRegRows.length,
        };
      }

      setRegistrationCounts(counts);
      setPreRegistrationCounts(preCounts);
      setDeleteBlockCounts((prev) => ({ ...prev, ...blockCounts }));
    } catch (err) {
      console.error("Error loading registration counts:", err);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await client.models.Event.list();
      if (result.errors && result.errors.length > 0) {
        console.error("GraphQL errors loading events:", result.errors);
        setError(`Failed to load events: ${result.errors[0].message}`);
        return;
      }
      if (result.data) {
        const sorted = [...result.data].sort(
          (a, b) =>
            new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime(),
        );
        setEvents(sorted as Event[]);

        // Load registration counts for competition events
        const competitionEventIds = sorted
          .filter((e) => e.eventType === "COMPETITION")
          .map((e) => e.id);
        if (competitionEventIds.length > 0) {
          await loadRegistrationCounts(competitionEventIds);
        }
      }
    } catch (err) {
      console.error("Error loading events:", err);
      setError("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showToast("Please select an image file", "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showToast("Image size must be less than 5MB", "error");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData({ ...formData, imageUrl: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate dates
    if (!formData.eventDate) {
      showToast("Event date is required", "error");
      return;
    }

    // Ordering guard. Nothing previously stopped a registration window from
    // being saved back-to-front, which is how the August 2026 event reached
    // the day before the comp with nobody noticing its timing was wrong (see
    // BACKLOG M0e). The equivalent check against the catch window
    // (competitionStartTime/competitionEndTime) lives in CompetitionSettingsForm.tsx,
    // since those fields are edited there, not on this form.
    const t = (v?: string) => (v ? new Date(v).getTime() : null);
    const regOpen = t(formData.registrationOpenTime);
    const regClose = t(formData.registrationCloseTime);
    if (regOpen !== null && regClose !== null && regClose <= regOpen) {
      showToast(
        "Registration close time must be after the open time",
        "error",
      );
      return;
    }

    try {
      setUploading(true);
      let imageUrl = formData.imageUrl;

      if (imageFile) {
        // Server-side validated presigned URL flow (admin-only, MIME + size enforced in Lambda)
        const result = await (client.queries as any).generateUploadUrl({
          purpose: 'EVENT_IMAGE',
          fileName: imageFile.name,
          contentType: imageFile.type,
          identityId: userId || '',
        });
        const uploadData = parseLambdaResponse(result?.data);
        if (!uploadData?.uploadUrl || !uploadData?.s3Key) throw new Error('Failed to get upload URL');

        await fetch(uploadData.uploadUrl, {
          method: 'PUT',
          body: imageFile,
          headers: { 'Content-Type': imageFile.type },
        });

        // Store the plain S3 key, not a baked/presigned URL — see
        // storageImageUrl.ts for why a stripped presigned URL never
        // actually works for this guest-readable-but-IAM-gated path.
        imageUrl = uploadData.s3Key;
      }

      // Convert club-time datetime-local values back to UTC ISO for GraphQL.
      // These inputs are rendered and edited in Australia/Sydney time
      // (toClubInputValue/fromClubInputValue), not the browser's local zone —
      // see frontend/src/utils/clubTime.ts.
      const eventData: any = {
        ...formData,
        eventDate: fromClubInputValue(formData.eventDate),
        registrationOpenTime: formData.registrationOpenTime
          ? fromClubInputValue(formData.registrationOpenTime)
          : undefined,
        registrationCloseTime: formData.registrationCloseTime
          ? fromClubInputValue(formData.registrationCloseTime)
          : undefined,
        preRegistrationEnabled: formData.preRegistrationEnabled ?? false,
        preRegistrationDeadline: formData.preRegistrationDeadline
          ? fromClubInputValue(formData.preRegistrationDeadline)
          : undefined,
        imageUrl,
      };

      // New competitions derive all five canonical times (competitionStartTime/
      // EndTime, registrationOpenTime/CloseTime, gracePeriodMinutes) from just
      // the event date, per the club's standard rules
      // (shared/competitionRules.ts) — rather than requiring an admin to type
      // five fields by hand, three of which (competitionStartTime/EndTime,
      // gracePeriodMinutes) aren't even editable on this form; they live on
      // the Setup screen, reachable only once the event exists. Only applies
      // to brand-new COMPETITION events, and only fills in fields the admin
      // hasn't already set on this form (registrationOpenTime/CloseTime) —
      // an admin who has typed an explicit window keeps it untouched.
      if (!editingEvent && formData.eventType === "COMPETITION" && eventData.eventDate) {
        const derived = deriveCompetitionTimes(eventData.eventDate);
        if (!eventData.registrationOpenTime) {
          eventData.registrationOpenTime = derived.registrationOpenTime;
        }
        if (!eventData.registrationCloseTime) {
          eventData.registrationCloseTime = derived.registrationCloseTime;
        }
        eventData.competitionStartTime = derived.competitionStartTime;
        eventData.competitionEndTime = derived.competitionEndTime;
        eventData.gracePeriodMinutes = derived.gracePeriodMinutes;
      }

      if (editingEvent) {
        if (imageFile && editingEvent.imageUrl) {
          try {
            const oldKey = extractStorageKey(editingEvent.imageUrl);
            if (oldKey) await remove({ path: oldKey });
          } catch (err) {
            console.error("Error deleting old image:", err);
          }
        }
        await client.models.Event.update({
          id: editingEvent.id,
          ...eventData,
        });
        showToast("Event updated successfully", "success");
      } else {
        const createResult = await client.models.Event.create({
          ...eventData,
          currentAttendees: 0,
        });
        if (createResult.errors && createResult.errors.length > 0) {
          console.error("GraphQL errors creating event:", createResult.errors);
          showToast(
            `Failed to create: ${createResult.errors[0].message}`,
            "error",
          );
          return;
        }
        showToast("Event created successfully", "success");
      }
      resetForm();
      await loadEvents();
    } catch (err) {
      console.error("Error saving event:", err);
      showToast("Failed to save event", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);

    setFormData({
      title: event.title,
      description: event.description,
      shortDescription: event.shortDescription || "",
      eventDate: toClubInputValue(event.eventDate),
      location: event.location || "",
      address: event.address || "",
      maxAttendees: event.maxAttendees,
      eventType: event.eventType,
      isActive: event.isActive,
      cost: event.cost,
      nonMemberPrice: event.nonMemberPrice,
      allowNonMembers: event.allowNonMembers || false,
      currency: event.currency,
      imageUrl: event.imageUrl || "",
      organizerName: event.organizerName || "",
      equipment: event.equipment || "",
      registrationOpenTime: toClubInputValue(event.registrationOpenTime),
      registrationCloseTime: toClubInputValue(event.registrationCloseTime),
      preRegistrationEnabled: event.preRegistrationEnabled ?? false,
      preRegistrationDeadline: toClubInputValue(
        event.preRegistrationDeadline,
      ),
    });
    setImagePreview("");
    setImageFile(null);
    setShowForm(true);
  };

  // Duplicate an existing event (typically last month's competition) as a
  // starting template for a new one. Defaults the new date to 4 weeks after
  // Duplicate an existing event (typically last month's competition) as a
  // starting template for a new one. Shifts the event date forward 4 weeks
  // — calendar days in CLUB TIME, not a fixed millisecond delta — then
  // re-derives every competition-timing field from that new date via
  // deriveCompetitionTimes, rather than shifting each field independently.
  // A fixed millisecond shift is wrong across a DST boundary: 4 weeks
  // (exactly 28*24h) lands an hour off whenever the shift crosses the
  // transition, e.g. duplicating a 07:00 AEST September competition into
  // October — after AEDT begins 4 Oct 2026 — would land on 06:00 or 08:00
  // instead of 07:00. Shifting the calendar date and re-deriving avoids
  // this entirely: the new date's own club-time offset is resolved fresh.
  // Opens in the create form (editingEvent stays null) so submitting makes
  // a new record — the admin can adjust the date first if 4 weeks isn't the
  // right gap — and the source event is never modified.
  const handleDuplicate = (event: Event) => {
    setEditingEvent(null);

    const dateShiftDays = (dateString: string | undefined | null, days: number): string => {
      if (!dateString) return "";
      const clubValue = toClubInputValue(dateString);
      if (!clubValue) return "";
      const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(clubValue);
      if (!match) return "";
      const [, y, mo, d, h, mi] = match;
      // Add days to the CALENDAR date only, in a UTC-based Date used purely
      // as a calendar calculator (never rendered/compared as a real
      // instant) — this correctly rolls over month/year boundaries without
      // touching the wall-clock time-of-day, which is what must be
      // preserved; the actual UTC instant for that time-of-day on the new
      // date is resolved afterwards via fromClubInputValue.
      const shifted = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d) + days, Number(h), Number(mi)));
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
    };

    const newEventDateClub = dateShiftDays(event.eventDate, 28);
    const newEventDateIso = fromClubInputValue(newEventDateClub);
    const isCompetition = event.eventType === "COMPETITION";
    // Re-derive the canonical competition times from the new date, rather
    // than shifting competitionStartTime/EndTime independently — those
    // aren't even on this form (Setup screen only) so there's nothing to
    // shift them FROM here; re-deriving is how a duplicated competition
    // gets them at all.
    const derived = isCompetition && newEventDateIso ? deriveCompetitionTimes(newEventDateIso) : null;

    setFormData({
      title: event.title,
      description: event.description,
      shortDescription: event.shortDescription || "",
      eventDate: newEventDateClub,
      location: event.location || "",
      address: event.address || "",
      maxAttendees: event.maxAttendees,
      eventType: event.eventType,
      isActive: event.isActive,
      cost: event.cost,
      nonMemberPrice: event.nonMemberPrice,
      allowNonMembers: event.allowNonMembers || false,
      currency: event.currency,
      // Image and mystery-fish/sticker setup are competition-specific, not
      // reusable month to month, so they intentionally are not copied.
      imageUrl: "",
      organizerName: event.organizerName || "",
      equipment: event.equipment || "",
      registrationOpenTime: derived
        ? toClubInputValue(derived.registrationOpenTime)
        : dateShiftDays(event.registrationOpenTime, 28),
      registrationCloseTime: derived
        ? toClubInputValue(derived.registrationCloseTime)
        : dateShiftDays(event.registrationCloseTime, 28),
      preRegistrationEnabled: event.preRegistrationEnabled ?? false,
      preRegistrationDeadline: dateShiftDays(event.preRegistrationDeadline, 28),
    });
    setImagePreview("");
    setImageFile(null);
    setShowForm(true);
    showToast(
      "Duplicated with dates shifted 4 weeks on — review before saving",
      "success",
    );
  };

  // Delete protection (R7.3): fresh paginated counts for one event, used
  // both to refresh the Delete button's disabled state and to re-check at
  // click time — the list-load count can go stale between page load and
  // the click (e.g. another admin adds a registration in between).
  const fetchDeleteBlockCounts = async (
    eventId: string,
  ): Promise<{ registrations: number; catches: number; preRegistrations: number }> => {
    const authClient = generateClient({ authMode: 'userPool' });

    const paginate = async (model: any): Promise<number> => {
      let rows: any[] = [];
      let token: string | null = null;
      do {
        const page: any = await model.list({
          filter: { eventId: { eq: eventId } },
          limit: 1000,
          ...(token ? { nextToken: token } : {}),
        });
        rows = rows.concat(page.data || []);
        token = page.nextToken ?? null;
      } while (token);
      return rows.length;
    };

    const [registrations, catches, preRegistrations] = await Promise.all([
      paginate(client.models.CompetitionDayRegistration as any),
      paginate(client.models.CatchSubmission as any),
      paginate(authClient.models.EventPreRegistration as any),
    ]);
    return { registrations, catches, preRegistrations };
  };

  const handleDelete = async (id: string) => {
    const event = events.find((e) => e.id === id);
    setDeleting(id);
    try {
      // Re-check at click time rather than trusting the counts loaded with
      // the list — they can go stale between page load and the click.
      const counts = await fetchDeleteBlockCounts(id);
      setDeleteBlockCounts((prev) => ({ ...prev, [id]: counts }));
      const blockers: string[] = [];
      if (counts.registrations > 0) blockers.push(`${counts.registrations} comp-day registration${counts.registrations === 1 ? '' : 's'}`);
      if (counts.catches > 0) blockers.push(`${counts.catches} catch submission${counts.catches === 1 ? '' : 's'}`);
      if (counts.preRegistrations > 0) blockers.push(`${counts.preRegistrations} RSVP${counts.preRegistrations === 1 ? '' : 's'}`);
      if (blockers.length > 0) {
        showToast(
          `Can't delete "${event?.title || 'this event'}": it has ${blockers.join(', ')}.`,
          "error",
        );
        return;
      }

      if (!window.confirm("Are you sure you want to delete this event?")) return;
      const result = await client.models.Event.delete({ id });
      if (result.errors && result.errors.length > 0) {
        showToast(`Failed to delete: ${result.errors[0].message}`, "error");
        return;
      }
      showToast("Event deleted", "success");
      await loadEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      showToast("Failed to delete event", "error");
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleActive = async (event: Event) => {
    try {
      const result = await client.models.Event.update({
        id: event.id,
        isActive: !event.isActive,
      });
      if (result.errors && result.errors.length > 0) {
        showToast(
          `Failed to update status: ${result.errors[0].message}`,
          "error",
        );
        return;
      }
      await loadEvents();
    } catch (err) {
      console.error("Error toggling active status:", err);
      showToast("Failed to update status", "error");
    }
  };

  // #134 D4: Reschedule moves eventDate and re-derives every dependent
  // competition time (registrationOpenTime/CloseTime, competitionStartTime/
  // EndTime) via rescheduleTimes, keeping each one's calendar offset from
  // eventDate in club time. Routed through the competition action API
  // (actionType RESCHEDULE) rather than a direct Event.update, so the write
  // goes through the same guarded, audited path as every other lifecycle
  // action (optimistic lock, attribute_exists(id), the audit entry).
  const openReschedule = (event: Event) => {
    setRescheduleEvent(event);
    setRescheduleDate(toClubInputValue(event.eventDate));
  };

  const handleReschedule = async () => {
    if (!rescheduleEvent || !rescheduleDate) return;
    const newEventDateIso = fromClubInputValue(rescheduleDate);
    if (!newEventDateIso) return;
    setRescheduling(true);
    try {
      const result = await (client.mutations as any).adjudicateCatch({
        actionType: "RESCHEDULE",
        eventId: rescheduleEvent.id,
        newEventDate: newEventDateIso,
      });
      if (result?.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.error || "Failed to reschedule");
      }
      showToast(data.message || "Competition rescheduled", "success");
      setRescheduleEvent(null);
      setRescheduleDate("");
      await loadEvents();
    } catch (err: any) {
      console.error("Error rescheduling event:", err);
      showToast(err.message || "Failed to reschedule", "error");
    } finally {
      setRescheduling(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      shortDescription: "",
      eventDate: "",
      location: "",
      address: "",
      maxAttendees: undefined,
      eventType: "SOCIAL",
      isActive: true,
      cost: undefined,
      nonMemberPrice: undefined,
      allowNonMembers: false,
      currency: "AUD",
      imageUrl: "",
      organizerName: "",
      equipment: "",
      registrationOpenTime: "",
      registrationCloseTime: "",
      preRegistrationEnabled: false,
      preRegistrationDeadline: "",
    });
    setEditingEvent(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview("");
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "ALL" || event.eventType === typeFilter;
    return matchesSearch && matchesType;
  });

  const formatDateTime = (dateString: string) => {
    return formatClubDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTypeBadge = (type: string) => {
    const styles = {
      MEETING: "bg-blue-100 text-blue-800",
      COMPETITION: "bg-red-100 text-red-800",
      SOCIAL: "bg-green-100 text-green-800",
      TRAINING: "bg-purple-100 text-purple-800",
      TRIP: "bg-yellow-100 text-yellow-800",
    };
    return styles[type as keyof typeof styles] || "bg-gray-100 text-gray-800";
  };

  // Zone label for the datetime-local inputs below, e.g. "(AEST)". Resolved
  // from the event's own date where one is set, so it reflects whichever
  // side of the 4 Oct 2026 DST transition that specific competition falls
  // on, rather than always reporting today's zone.
  const clubZoneLabel = `(${getClubTimezoneAbbreviation(
    formData.eventDate ? new Date(fromClubInputValue(formData.eventDate) || Date.now()) : undefined,
  )})`;

  const confirmCopy = (() => {
    if (!confirmAction) return null;
    const { event } = confirmAction;
    return event.isActive
      ? { title: `Deactivate ${event.title}?`, message: "Deactivating hides this competition from check-in and catch pages.", label: "Deactivate", destructive: true }
      : { title: `Activate ${event.title}?`, message: "Activating shows this competition on check-in and catch pages again.", label: "Activate", destructive: false };
  })();

  return (
    <div className="space-y-6">
      {confirmAction && confirmCopy && (
        <ConfirmActionDialog
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmLabel={confirmCopy.label}
          destructive={confirmCopy.destructive}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => {
            const { event } = confirmAction;
            setConfirmAction(null);
            handleToggleActive(event);
          }}
        />
      )}
      {rescheduleEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" aria-labelledby="reschedule-title" className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 id="reschedule-title" className="text-lg font-bold text-gray-900">Reschedule {rescheduleEvent.title}?</h2>
            <p className="mt-2 text-sm text-gray-700">
              Every competition time moves to keep the same offset from the new fishing start.
            </p>
            <label htmlFor="reschedule-date" className="mt-4 block text-sm font-medium text-gray-700">
              New fishing start
            </label>
            <input
              id="reschedule-date"
              type="datetime-local"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue text-sm"
            />
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => { setRescheduleEvent(null); setRescheduleDate(""); }}
                className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReschedule}
                disabled={rescheduling || !rescheduleDate}
                className="min-h-[44px] rounded-lg px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:bg-gray-300"
              >
                {rescheduling ? "Rescheduling…" : "Reschedule"}
              </button>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div
          role="alert" aria-live="assertive"
          className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
            toast.type === "success" ? "bg-green-600" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Event Management</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCatchQr(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
              />
            </svg>
            Get Catch QR
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700"
          >
            <svg
              className="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Event
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search events..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Types</option>
              <option value="MEETING">Meeting</option>
              <option value="COMPETITION">Competition</option>
              <option value="SOCIAL">Social</option>
              <option value="TRAINING">Training</option>
              <option value="TRIP">Trip</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadEvents}
              className="text-fishing-blue hover:text-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No events found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredEvents.map((event) => (
              <div key={event.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start gap-4">
                  <StorageImage
                    storageKey={event.imageUrl}
                    alt={event.title}
                    className="w-24 h-24 object-cover rounded flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {event.title}
                      </h3>
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadge(event.eventType)}`}
                      >
                        {event.eventType}
                      </span>
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          event.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {event.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {event.shortDescription ||
                        event.description.substring(0, 150) + "..."}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>📅 {formatDateTime(event.eventDate)}</span>
                      {event.location && <span>📍 {event.location}</span>}
                      {event.maxAttendees && (
                        <span>
                          {/* #88: currentAttendees is never incremented
                              (always 0), so it's no longer shown or relied
                              on. registrationCounts[event.id].total is the
                              real count — this component already loads it
                              per event (loadRegistrationCounts) for the
                              "Manage Check-Ins" pending badge below, so it's
                              cheap to show here too; otherwise just Max N. */}
                          {registrationCounts[event.id]
                            ? `${registrationCounts[event.id].total}/${event.maxAttendees} attendees`
                            : `Max ${event.maxAttendees} attendees`}
                        </span>
                      )}
                      {event.cost && event.cost > 0 && (
                        <span className="text-fishing-green font-medium">
                          ${event.cost}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/*
                  Actions on their own row beneath the details, title given
                  full width above. Previously these lived in an `ml-4` flex
                  cluster alongside the title (8 controls for a competition
                  row), squeezing the title into a three-line wrap on
                  anything narrower than a wide desktop.
                */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-2">
                    {event.eventType === "COMPETITION" && (
                      <>
                        {/*
                          Derived phase chip (#134 D4), replacing the
                          "Check-in: Open (scheduled)/(forced)/Closed" status
                          that read manual check-in override. One label for
                          the competition's whole lifecycle rather than a
                          check-in-only status, computed the same way as
                          every other screen (deriveLifecycle), so this row
                          cannot show a different answer than Setup or
                          Adjudication.
                        */}
                        {(() => {
                          const phase = deriveLifecycle(event, new Date()).phase;
                          return (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${PHASE_COLOUR[phase]}`}>
                              {PHASE_LABEL[phase]}
                            </span>
                          );
                        })()}
                        {/*
                          Timing-completeness warning. competitionStartTime /
                          competitionEndTime live on the Setup screen, not this
                          form, and when unset the catch-submission window is
                          not enforced (deriveLifecycle fails closed, but that
                          is invisible from here otherwise).
                        */}
                        {(!event.competitionStartTime ||
                          !event.competitionEndTime) && (
                          <span
                            title="Fishing start/end times are not set on the Setup screen, so the catch submission window is not enforced for this competition."
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800"
                          >
                            ⚠ Timing incomplete
                          </span>
                        )}
                        {/*
                          #134 D4: Reschedule replaces Force open/Cancel
                          override. It re-derives every dependent time from
                          the new fishing start rather than leaving an
                          override that only ever forced check-in open.
                        */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openReschedule(event);
                          }}
                          className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                          title="Move the fishing start and re-derive every dependent time"
                          aria-label={`Reschedule ${event.title}`}
                        >
                          Reschedule
                        </button>
                        <button
                          onClick={() => {
                            window.history.pushState(
                              {},
                              "",
                              `/admin/competition-day/${event.id}`,
                            );
                            window.location.reload();
                          }}
                          className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            registrationCounts[event.id]?.pending > 0
                              ? "bg-orange-100 text-orange-800 hover:bg-orange-200 border-2 border-orange-300"
                              : "bg-green-100 text-green-800 hover:bg-green-200 border-2 border-green-300"
                          }`}
                          title="View Competition Check-In Dashboard"
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                            />
                          </svg>
                          <span>
                            Manage Check-Ins
                            {registrationCounts[event.id] && (
                              <span className="ml-1 font-bold">
                                ({registrationCounts[event.id].pending} pending)
                              </span>
                            )}
                          </span>
                        </button>
                        {event.preRegistrationEnabled && (
                          <button
                            onClick={() => {
                              setShowPreRegModal({
                                eventId: event.id,
                                eventTitle: event.title,
                                eventDate: event.eventDate,
                              });
                            }}
                            className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              preRegistrationCounts[event.id]?.pending > 0
                                ? "bg-purple-100 text-purple-800 hover:bg-purple-200 border-2 border-purple-300"
                                : "bg-indigo-100 text-indigo-800 hover:bg-indigo-200 border-2 border-indigo-300"
                            }`}
                            title="Manage member RSVPs — advance headcount"
                          >
                            <svg
                              className="h-4 w-4 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            <span>
                              Manage RSVPs
                              {preRegistrationCounts[event.id] && (
                                <span className="ml-1 font-bold">
                                  ({preRegistrationCounts[event.id].pending}{" "}
                                  pending)
                                </span>
                              )}
                            </span>
                          </button>
                        )}
                        <button
                          onClick={() => setAdjudicationEvent(event)}
                          className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                          title="Competition settings (times, fees, mystery fish, sticker, RSVP)"
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Setup
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setConfirmAction({ event })}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title={event.isActive ? "Deactivate" : "Activate"}
                      aria-label={`${event.isActive ? "Deactivate" : "Activate"} ${event.title}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDuplicate(event)}
                      className="p-2 text-gray-500 hover:text-gray-700"
                      title="Duplicate as template for next month"
                      aria-label={`Duplicate as template for next month: ${event.title}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => event.eventType === "COMPETITION" ? setAdjudicationEvent(event) : handleEdit(event)}
                      className="p-2 text-fishing-blue hover:text-blue-700"
                      title="Edit"
                      aria-label={`Edit ${event.title}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(event.id)}
                      disabled={deleting === event.id || !!(deleteBlockCounts[event.id] && (deleteBlockCounts[event.id].registrations > 0 || deleteBlockCounts[event.id].catches > 0 || deleteBlockCounts[event.id].preRegistrations > 0))}
                      className="p-2 text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:hover:text-gray-300 disabled:cursor-not-allowed"
                      title={
                        deleteBlockCounts[event.id] && (deleteBlockCounts[event.id].registrations > 0 || deleteBlockCounts[event.id].catches > 0 || deleteBlockCounts[event.id].preRegistrations > 0)
                          ? "Can't delete: this event has registrations, catches or RSVPs. Deactivate it instead."
                          : "Delete"
                      }
                      aria-label={`Delete ${event.title}`}
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">
                {editingEvent ? "Edit Event" : "New Event"}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label htmlFor="event-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  id="event-title"
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label htmlFor="event-description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  id="event-description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image (Optional)
                </label>
                <div className="mt-1 flex items-center gap-4">
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  {(imagePreview || editingEvent?.imageUrl) && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Upload an image (max 5MB)
                </p>
                {imagePreview ? (
                  <div className="mt-3">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-h-48 rounded border border-gray-300"
                    />
                  </div>
                ) : editingEvent?.imageUrl ? (
                  <div className="mt-3">
                    <StorageImage
                      storageKey={editingEvent.imageUrl}
                      alt="Current event image"
                      className="max-h-48 rounded border border-gray-300"
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <label htmlFor="event-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type *
                </label>
                <select
                  id="event-type"
                  required
                  value={formData.eventType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      eventType: e.target.value as any,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                >
                  <option value="MEETING">Meeting</option>
                  <option value="COMPETITION">Competition</option>
                  <option value="SOCIAL">Social</option>
                  <option value="TRAINING">Training</option>
                  <option value="TRIP">Trip</option>
                </select>
              </div>

              <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
                <div>
                  <label htmlFor="event-date-time" className="block text-sm font-medium text-gray-700 mb-1">
                    Event Date &amp; Time * <span className="font-normal text-gray-500">{clubZoneLabel}</span>
                  </label>
                  <input
                    id="event-date-time"
                    type="datetime-local"
                    required
                    value={formData.eventDate}
                    onChange={(e) =>
                      setFormData({ ...formData, eventDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              {/*
                #67 R2.1-R2.3: the registration window, non-member
                registration and member RSVP fields moved to
                CompetitionSettingsForm, reachable via Edit or Setup once
                the competition exists — they are not needed to create one.
                New competitions still derive their canonical times
                (registrationOpenTime/CloseTime, competitionStartTime/
                EndTime, gracePeriodMinutes) from Event Date & Time alone
                (deriveCompetitionTimes, in handleSubmit), unless the admin
                opens Settings afterward to change them.
              */}
              {formData.eventType === "COMPETITION" && !editingEvent && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  {/*
                    #134 e2e fix: who may enter is decided when the competition
                    is created (as it was before #67), so guests and RSVP do not
                    silently default to off. Everything else (times, mystery
                    fish, sticker) is in Competition Settings afterwards.
                  */}
                  <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
                    <div>
                      <label htmlFor="create-allow-non-members" className="flex items-center gap-2 min-h-[44px]">
                        <input
                          id="create-allow-non-members"
                          type="checkbox"
                          checked={!!formData.allowNonMembers}
                          onChange={(e) => setFormData({ ...formData, allowNonMembers: e.target.checked })}
                          className="h-5 w-5 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                        />
                        <span className="text-sm font-medium text-gray-700">Allow Non-Members</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-7">Guests can check in and enter catches.</p>
                    </div>
                    <div>
                      <label htmlFor="create-non-member-price" className="block text-sm font-medium text-gray-700 mb-1">
                        Non-Member Price ($)
                      </label>
                      <input
                        id="create-non-member-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.nonMemberPrice ?? ""}
                        onChange={(e) => setFormData({ ...formData, nonMemberPrice: e.target.value === "" ? undefined : parseFloat(e.target.value) || 0 })}
                        disabled={!formData.allowNonMembers}
                        className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="create-enable-rsvp" className="flex items-center gap-2 min-h-[44px]">
                      <input
                        id="create-enable-rsvp"
                        type="checkbox"
                        checked={!!formData.preRegistrationEnabled}
                        onChange={(e) => setFormData({ ...formData, preRegistrationEnabled: e.target.checked })}
                        className="h-5 w-5 rounded border-gray-300 text-fishing-blue focus:ring-fishing-blue"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable member RSVP</span>
                    </label>
                    <p className="text-xs text-gray-500 ml-7">Members can reserve a spot in advance. This is not comp-day sign-on.</p>
                  </div>
                  <p className="text-sm text-gray-700">
                    Times, mystery fish and the sticker are set in Competition
                    Settings once the competition is created: use the event
                    row's Setup button after saving.
                  </p>
                </div>
              )}

              <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    placeholder="e.g., Riverside Drive opposite Tumbulgum Tavern"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Member Cost (AUD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cost !== undefined ? formData.cost : 10}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cost: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-fishing-blue focus:ring-fishing-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {uploading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    `${editingEvent ? "Update" : "Create"} Event`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreRegModal && (
        <PreRegistrationApproval
          eventId={showPreRegModal.eventId}
          eventTitle={showPreRegModal.eventTitle}
          eventDate={showPreRegModal.eventDate}
          onClose={() => {
            setShowPreRegModal(null);
            loadEvents(); // Reload to update counts
          }}
        />
      )}

      {adjudicationEvent && (
        <CompetitionSettingsForm
          event={adjudicationEvent}
          isOpen={true}
          onClose={() => setAdjudicationEvent(null)}
          onSave={() => {
            setAdjudicationEvent(null);
            loadEvents();
          }}
        />
      )}

      {/* Catch-Submission QR Modal — one single, competition-agnostic QR
          (not per-event, unlike the check-in QR above), meant to be printed
          once and displayed on the check-in banner and the brag mat. */}
      {showCatchQr && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCatchQr(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">Catch Submission QR</h3>
                <button onClick={() => setShowCatchQr(false)} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                One QR for every competition — print it once and put it on the check-in banner and the brag mat.
              </p>
              <div className="flex justify-center mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                <QRCodeSVG
                  value={`${window.location.origin}/catch`}
                  size={220}
                  level="M"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs text-gray-500 mb-4 break-all">
                {`${window.location.origin}/catch`}
              </p>
              <p className="text-xs text-gray-400">Scan to submit a catch for whichever competition is currently open</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
